import React, { useState } from "react";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useToast } from "src/components/ui/toast";
import { Button } from "src/components/ui/button";
import { updatePassword } from "src/apis/auth";

function PasswordInput({ id, label, value, onChange, disabled, placeholder }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="space-y-2">
      <label htmlFor={id} className="text-[13px] font-semibold text-slate-700">
        {label}
      </label>
      <div className="relative max-w-md">
        <input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 pr-10 text-[14px] text-slate-800 placeholder:text-slate-400 transition-colors focus:border-[#4b5bed] focus:outline-none focus:ring-2 focus:ring-[#4b5bed]/20 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 disabled:pointer-events-none"
          aria-label={visible ? "Hide password" : "Show password"}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

export default function ResetPasswordPage() {
  const { toast } = useToast();

  const [form, setForm] = useState({
    currentPassword: "",
    password: "",
    passwordConfirmation: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  function set(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.currentPassword || !form.password || !form.passwordConfirmation) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "All fields are required.",
      });
      return;
    }

    if (form.password !== form.passwordConfirmation) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "New password and confirmation do not match.",
      });
      return;
    }

    if (form.password.length < 6) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "New password must be at least 6 characters.",
      });
      return;
    }

    setIsLoading(true);
    try {
      await updatePassword(form);
      toast({
        title: "Password updated",
        description: "Your password has been changed successfully.",
      });
      setForm({ currentPassword: "", password: "", passwordConfirmation: "" });
    } catch (error) {
      const msg =
        error.response?.data?.errors?.[0] ||
        "Failed to update password. Please try again.";
      toast({ variant: "destructive", title: "Error", description: msg });
    } finally {
      setIsLoading(false);
    }
  }

  const canSubmit =
    !isLoading &&
    form.currentPassword.trim() &&
    form.password.trim() &&
    form.passwordConfirmation.trim();

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900">
          Reset Password
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Update your account password. You'll need to enter your current password to confirm the change.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <PasswordInput
          id="current_password"
          label="Current Password"
          value={form.currentPassword}
          onChange={set("currentPassword")}
          disabled={isLoading}
          placeholder="Enter your current password"
        />

        <PasswordInput
          id="new_password"
          label="New Password"
          value={form.password}
          onChange={set("password")}
          disabled={isLoading}
          placeholder="At least 6 characters"
        />

        <PasswordInput
          id="password_confirmation"
          label="Confirm New Password"
          value={form.passwordConfirmation}
          onChange={set("passwordConfirmation")}
          disabled={isLoading}
          placeholder="Re-enter your new password"
        />

        <div className="flex justify-start">
          <Button type="submit" disabled={!canSubmit} size="sm">
            {isLoading ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                Updating Password
              </>
            ) : (
              "Update Password"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
